import { CSSProperties, FC, ReactNode } from 'react';
import { Divider } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import SmallHelpButton from '@/shared/components/buttons/SmallHelpButton';
import { MiddleDot } from '@/shared/components/typography';
import ResourceAssociations from '@/shared/components/workspace-resources/ResourceAssociations';
import { ResourceActivityResourceType } from '@/types';

import {
    SMALL_RESOURCE_FOOTER_HEIGHT,
    SMALL_RESOURCE_FOOTER_MIN_HEIGHT,
    SMALL_RESOURCE_FOOTER_PADDING,
} from './constants';

interface SmallResourceFooterProps {
    associationResource?: {
        id: string;
        type: ResourceActivityResourceType;
        updatedAt?: Date | string;
    };
    left?: ReactNode;
    center?: ReactNode;
    right?: ReactNode;
    showHelp?: boolean;
    showHelpDivider?: boolean;
    padding?: CSSProperties['padding'];
    backgroundColor?: CSSProperties['backgroundColor'];
    className?: string;
    style?: CSSProperties;
}

const SmallResourceFooter: FC<SmallResourceFooterProps> = ({
    associationResource,
    left,
    center,
    right,
    showHelp = true,
    showHelpDivider = false,
    padding = SMALL_RESOURCE_FOOTER_PADDING,
    backgroundColor,
    className,
    style,
}): JSX.Element => {
    const { token } = useAntDesignTheme();

    return (
        <div
            className={className}
            data-small-resource-footer='true'
            style={{
                alignItems: 'center',
                backgroundColor: backgroundColor ?? token.colorBgContainer,
                borderTop: `1px solid ${token.colorBorder}`,
                boxSizing: 'border-box',
                display: 'grid',
                flex: 'none',
                gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
                height: SMALL_RESOURCE_FOOTER_HEIGHT,
                minHeight: SMALL_RESOURCE_FOOTER_MIN_HEIGHT,
                padding,
                width: '100%',
                ...style,
            }}
        >
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifySelf: 'start',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                {associationResource ? (
                    <>
                        <ResourceAssociations
                            resourceId={associationResource.id}
                            resourceType={associationResource.type}
                            resourceUpdatedAt={associationResource.updatedAt}
                        />
                        {left !== undefined && left !== null ? <MiddleDot horizontalMargin='0.5rem' /> : null}
                    </>
                ) : null}
                {left}
            </div>
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                    textAlign: 'center',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {center}
            </div>
            <div
                style={{
                    alignItems: 'center',
                    display: 'flex',
                    justifySelf: 'end',
                    lineHeight: 0,
                    maxWidth: '100%',
                    minWidth: 0,
                    overflow: 'hidden',
                }}
            >
                {right}
                {showHelp ? (
                    <>
                        {showHelpDivider && right !== undefined ? (
                            <Divider style={{ marginInline: '0.5rem' }} type='vertical' />
                        ) : null}
                        <span
                            style={{
                                alignItems: 'center',
                                display: 'inline-flex',
                                marginLeft: right !== undefined && !showHelpDivider ? '0.5rem' : undefined,
                            }}
                        >
                            <SmallHelpButton />
                        </span>
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default SmallResourceFooter;
